import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  Sponsor,
  SponsorWithRates,
  SponsorFilters,
  PaginatedSponsorsResponse,
  SponsorCreateRequest,
  SponsorUpdateRequest,
  SponsorPointRate,
  SponsorPointRateCreateRequest,
  SponsorPointRateUpdateRequest,
  SponsorLocation,
  SponsorLocationCreateRequest,
  SponsorLocationUpdateRequest,
  SponsorRepresentative,
  SponsorRepresentativeCreateRequest,
  SponsorRepresentativeUpdateRequest,
  LogoUploadUrlResponse
} from '../models/sponsor.model';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface PaginatedApiResponse {
  success: boolean;
  data?: {
    sponsors: Sponsor[];
    total: number;
    page: number;
    limit: number;
  };
  error?: string;
}

/**
 * Sponsors Service (Admin)
 *
 * Service for managing ADN Sponsors in the admin portal.
 * Communicates with /admin/sponsors endpoints.
 * Includes rate, location, representative management and logo upload methods.
 */
@Injectable({
  providedIn: 'root'
})
export class SponsorsService {
  private readonly apiUrl = `${environment.apiUrl}/admin/sponsors`;
  private readonly fileUploadsUrl = `${environment.apiUrl}/admin/file-uploads`;

  constructor(private http: HttpClient) {}

  // ============================================
  // SPONSOR CRUD
  // ============================================

  /**
   * List sponsors with optional filters and pagination
   */
  listSponsors(filters?: SponsorFilters): Observable<PaginatedSponsorsResponse> {
    const params = new URLSearchParams();

    if (filters?.page !== undefined && filters.page !== null) {
      params.append('page', filters.page.toString());
    }
    if (filters?.limit !== undefined && filters.limit !== null) {
      params.append('limit', filters.limit.toString());
    }
    if (filters?.query) {
      params.append('query', filters.query);
    }

    const queryString = params.toString();
    const url = queryString ? `${this.apiUrl}?${queryString}` : this.apiUrl;

    return this.http.get<PaginatedApiResponse>(url).pipe(
      map((response) => {
        if (response.success && response.data) {
          return {
            sponsors: response.data.sponsors,
            total: response.data.total,
            page: response.data.page,
            pageSize: response.data.limit
          };
        }
        return { sponsors: [], total: 0, page: 1, pageSize: 20 };
      })
    );
  }

  /**
   * Get a single sponsor by ID with its rates
   */
  getSponsor(id: string): Observable<SponsorWithRates | null> {
    return this.http.get<ApiResponse<SponsorWithRates>>(`${this.apiUrl}/${id}`).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        return null;
      })
    );
  }

  /**
   * Create a new sponsor
   */
  createSponsor(data: SponsorCreateRequest): Observable<Sponsor> {
    return this.http.post<ApiResponse<Sponsor>>(this.apiUrl, data).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to create sponsor');
      })
    );
  }

  /**
   * Update an existing sponsor
   */
  updateSponsor(id: string, data: SponsorUpdateRequest): Observable<Sponsor> {
    return this.http.put<ApiResponse<Sponsor>>(`${this.apiUrl}/${id}`, data).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to update sponsor');
      })
    );
  }

  /**
   * Archive a sponsor (soft delete)
   */
  archiveSponsor(id: string): Observable<void> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`).pipe(
      map((response) => {
        if (!response.success) {
          throw new Error(response.error || 'Failed to archive sponsor');
        }
      })
    );
  }

  // ============================================
  // RATE MANAGEMENT
  // ============================================

  /**
   * Get all rates for a sponsor
   */
  getSponsorRates(sponsorId: string): Observable<SponsorPointRate[]> {
    return this.http.get<ApiResponse<{ rates: SponsorPointRate[] }>>(`${this.apiUrl}/${sponsorId}/rates`).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data.rates;
        }
        return [];
      })
    );
  }

  /**
   * Create a new rate for a sponsor
   */
  createSponsorRate(sponsorId: string, data: SponsorPointRateCreateRequest): Observable<SponsorPointRate> {
    return this.http.post<ApiResponse<SponsorPointRate>>(`${this.apiUrl}/${sponsorId}/rates`, data).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to create sponsor rate');
      })
    );
  }

  /**
   * Update a future rate
   */
  updateSponsorRate(sponsorId: string, rateId: string, data: SponsorPointRateUpdateRequest): Observable<SponsorPointRate> {
    return this.http.put<ApiResponse<SponsorPointRate>>(`${this.apiUrl}/${sponsorId}/rates/${rateId}`, data).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to update sponsor rate');
      })
    );
  }

  /**
   * End an active rate by setting its effective_end date
   */
  endSponsorRate(sponsorId: string, rateId: string, endDate: string): Observable<SponsorPointRate> {
    return this.http.post<ApiResponse<SponsorPointRate>>(`${this.apiUrl}/${sponsorId}/rates/${rateId}/end`, { endDate }).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to end sponsor rate');
      })
    );
  }

  // ============================================
  // LOCATION MANAGEMENT
  // ============================================

  /**
   * List all locations for a sponsor
   */
  listSponsorLocations(sponsorId: string): Observable<SponsorLocation[]> {
    return this.http.get<ApiResponse<{ locations: SponsorLocation[] }>>(`${this.apiUrl}/${sponsorId}/locations`).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data.locations;
        }
        return [];
      })
    );
  }

  /**
   * Create a new location for a sponsor
   */
  createSponsorLocation(sponsorId: string, data: SponsorLocationCreateRequest): Observable<SponsorLocation> {
    return this.http.post<ApiResponse<SponsorLocation>>(`${this.apiUrl}/${sponsorId}/locations`, data).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to create sponsor location');
      })
    );
  }

  /**
   * Update an existing location
   */
  updateSponsorLocation(sponsorId: string, locationId: string, data: SponsorLocationUpdateRequest): Observable<SponsorLocation> {
    return this.http.put<ApiResponse<SponsorLocation>>(`${this.apiUrl}/${sponsorId}/locations/${locationId}`, data).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to update sponsor location');
      })
    );
  }

  /**
   * Archive a sponsor location (soft delete)
   */
  archiveSponsorLocation(sponsorId: string, locationId: string): Observable<void> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${sponsorId}/locations/${locationId}`).pipe(
      map((response) => {
        if (!response.success) {
          throw new Error(response.error || 'Failed to archive sponsor location');
        }
      })
    );
  }

  // ============================================
  // REPRESENTATIVE MANAGEMENT
  // ============================================

  /**
   * List all representatives for a sponsor
   */
  listSponsorRepresentatives(sponsorId: string): Observable<SponsorRepresentative[]> {
    return this.http.get<ApiResponse<{ representatives: SponsorRepresentative[] }>>(`${this.apiUrl}/${sponsorId}/representatives`).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data.representatives;
        }
        return [];
      })
    );
  }

  /**
   * Create a new representative for a sponsor
   */
  createSponsorRepresentative(sponsorId: string, data: SponsorRepresentativeCreateRequest): Observable<SponsorRepresentative> {
    return this.http.post<ApiResponse<SponsorRepresentative>>(`${this.apiUrl}/${sponsorId}/representatives`, data).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to create sponsor representative');
      })
    );
  }

  /**
   * Update an existing representative
   */
  updateSponsorRepresentative(sponsorId: string, repId: string, data: SponsorRepresentativeUpdateRequest): Observable<SponsorRepresentative> {
    return this.http.put<ApiResponse<SponsorRepresentative>>(`${this.apiUrl}/${sponsorId}/representatives/${repId}`, data).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to update sponsor representative');
      })
    );
  }

  /**
   * Archive a sponsor representative (soft delete)
   */
  archiveSponsorRepresentative(sponsorId: string, repId: string): Observable<void> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${sponsorId}/representatives/${repId}`).pipe(
      map((response) => {
        if (!response.success) {
          throw new Error(response.error || 'Failed to archive sponsor representative');
        }
      })
    );
  }

  // ============================================
  // LOGO UPLOAD
  // ============================================

  /**
   * Get a pre-signed URL for uploading a sponsor logo
   */
  getLogoUploadUrl(filename: string, contentType?: string): Observable<LogoUploadUrlResponse> {
    return this.http
      .post<ApiResponse<LogoUploadUrlResponse>>(`${this.fileUploadsUrl}/sponsor-logo`, { filename, contentType })
      .pipe(
        map((response) => {
          if (response.success && response.data) {
            return response.data;
          }
          throw new Error(response.error || 'Failed to get upload URL');
        })
      );
  }
}
